/**
 * "Plant now" (E5): the one-tap button on a Planning plant's card. The flow, apart from the screen so it can be tested
 * without drawing one: it records the stage the server chose for the plant (the plants brief's plantNow, by the plant's
 * planting window method) as it begins today, the same call Record a stage makes (POST /stage-marks), and hands back
 * how to take it back. No dialog: the Undo is the safety.
 *
 * A plant in the Seed vault (D69, the plant's move_out) is asked the one allowed question first, "Which bed did they go
 * into?" (features/seed-orders WhichBedSheet), exactly as Record a stage asks it;
 * closing the question records nothing. Its Undo takes the stage mark back; the server, which stored that
 * the mark moved the plant, puts it back in the Seed vault (D69). The app sends nothing extra.
 *
 * The app decides nothing here: which stage, and whether a bed is needed, are the server's; the day is the garden's own
 * (the server's, from its time zone).
 *
 * Where: PlantNowButton (the Planning bar on the plant card).
 *
 * @module plantNow
 */

import { stageAskOf, type BedAnswer, type BedAsk } from '../../seed-orders/lib/whichBed.ts';

/** The server's plantNow for a plant (plants brief): the stage the button records. */
export interface PlantNow {
  /** The gardener's catalogue stage to record. */
  stageId: number;
  /** Its catalogue type (SEEDING, TRANSPLANTING). */
  stageType: string;
  /** The planting window method it follows (direct_seed, transplant, start_indoors). */
  method: string;
}

/** What the flow needs from outside, so a test can give it fakes. */
export interface PlantNowDeps {
  /** Records a stage on a day, for a bed when given (api/stageMarks addStageMark); resolves with the new entry. */
  addMark: (plantObjId: number, stageId: number, date: string, locationId: number | null) => Promise<{ mark_id: number }>;
  /** Takes an entry back (api/stageMarks removeStageMark); the server puts a vault plant back in the vault when this mark moved it out (D69). */
  removeMark: (plantObjId: number, markId: number) => Promise<void>;
  /** Asks "Which bed did they go into?" (seed-orders askWhichBed). */
  askBed: (ask: BedAsk) => Promise<BedAnswer>;
}

/** What a tap came to. */
export type PlantNowResult =
  | { recorded: false }
  | { recorded: true; markId: number; movedToBed: boolean; undo: () => Promise<void> };

/**
 * Records the plant's planting stage today, asking which bed first for a Seed vault plant.
 *
 * @param plant - The plant: its id and its move_out (the server's), null for a plant not in the vault.
 * @param plantNow - The server's stage for it.
 * @param today - The garden's own day, YYYY-MM-DD.
 * @param stageName - The stage as the gardener names it, for the question's line.
 * @param deps - The calls it makes.
 * @returns Whether it recorded, and how to undo it. Rejects when the server refuses or fails (the caller says so).
 */
export async function plantNowFlow(
  plant: { plant_obj_id: number; move_out?: { bedAsked: number[]; indoors: number[] } | null },
  plantNow: PlantNow,
  today: string,
  stageName: string | null,
  deps: PlantNowDeps,
): Promise<PlantNowResult> {
  const asks = stageAskOf(plant.move_out, plantNow.stageId);
  let locationId: number | null = null;
  if (asks) {
    const answer = await deps.askBed({ sub: stageName, indoors: asks === 'indoors' });
    if (!answer) return { recorded: false };
    locationId = 'bedId' in answer ? answer.bedId : null;
  }
  const mark = await deps.addMark(plant.plant_obj_id, plantNow.stageId, today, locationId);
  return {
    recorded: true,
    markId: mark.mark_id,
    movedToBed: locationId !== null,
    undo: () => deps.removeMark(plant.plant_obj_id, mark.mark_id),
  };
}
